import { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router";
import { useSights } from "../context/SightContext";
import {
  GridIcon,
  HorizontaLDots,
  // PieChartIcon,
  // PlugInIcon,
  // UserCircleIcon,
  NotificationIcondark,
  NotificationIconlight,
  SupportIcondark,
  SupportIconlight,
  BookDemoIcondark,
  BookDemoIconlight,
  // FileIcon,
  // TimeIcon,
  // ShootingStarIcon,
} from "../icons";

import { useSidebar } from "../context/SidebarContext";

import { useAuth } from "../context/AuthContext";
import { useEnhancedPermissions } from "../hooks/useEnhancedPermissions";
import { useActiveStore } from "../context/ActiveStoreContext";
import IsightOpenButton from "./IsightOpenButton";
import ScalekitOpenButton from "./ScalekitOpenButton";
import {
  ChartArea,
  ChartAreaIcon,
  ChartBar,
  ChartBarBigIcon,
  ChartColumn,
  ChartColumnStackedIcon,
  ChartNetworkIcon,
  ChartNoAxesCombinedIcon,
  GalleryThumbnails,
  GitGraph,
  LucideGitGraph,
  SpotlightIcon,
  Megaphone,
  Package,
  Users,
  Store,
  Sparkles,
  CreditCard
} from "lucide-react";
import { Bars4Icon } from "@heroicons/react/24/solid";
import axios from "../lib/axios";
import Loader from "../components/common/Loader";
import { DeleteConfirmModal } from "../admin-panel/components/DeleteConfirmModal";
import { useFeatureAccess } from "../context/FeatureAccessContext";

type NavItem = {
  name: string;
  icon: React.ReactNode;
  path?: string;
  subItems?: { name: string; path: string; pro?: boolean; new?: boolean }[];
};

const navItems: NavItem[] = [
  {
    name: 'Infrastructure',
    icon: <ChartAreaIcon className="w-5 h-5" />, // using an appropriate icon
    path: '/scaleboard/infrastructure',
  },
];

const othersItems: NavItem[] = [];

export default function AppSidebar({
  disableNavigation,
}: {
  disableNavigation?: boolean;
}) {
  const {
    isExpanded,
    isHovered,
    isMobileOpen,
    setIsHovered,
    toggleMobileSidebar,
  } = useSidebar();
  const { sights, loadSights, createSight, updateSight, deleteSight } =
    useSights();
  const location = useLocation();
  const navigate = useNavigate();
  const [isAddingSight, setIsAddingSight] = useState(false);
  const [newSightName, setNewSightName] = useState("");
  const [editingSightId, setEditingSightId] = useState<number | null>(null);
  const [editSightName, setEditSightName] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [sightPendingDelete, setSightPendingDelete] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [isDeletingSight, setIsDeletingSight] = useState(false);
  const [protectedSightAction, setProtectedSightAction] = useState<
    "edit" | "delete" | null
  >(null);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [sightNameDuplicateConfirm, setSightNameDuplicateConfirm] = useState<
    string | null
  >(null);
  const [isCreatingSight, setIsCreatingSight] = useState(false);
  const { currentShop, isFreePlan, user, stores } = useAuth();
  const { activeStore, refreshStores } = useActiveStore();
  const { checkPermission } = useEnhancedPermissions();
  const { hasFeature, showUpgradeModal: openUpgradeModal, featureLimitReached } = useFeatureAccess();
  const [isSightEnabled, setIsSightEnabled] = useState<boolean>(true);
  const [hasAcceptedStores, setHasAcceptedStores] = useState<boolean>(false);
  const [hasRazorpayIntegration, setHasRazorpayIntegration] = useState<boolean>(false);
  const tenantKey = import.meta.env.VITE_REACT_APP_TENANT;

  // Derive connected & owner status with fallback chain:
  // 1) activeStore from ActiveStoreContext (primary)
  // 2) localStorage cached store (bridge between contexts)
  // 3) stores from AuthContext (ultimate fallback)
  const fallbackStore = (() => {
    try {
      const cached = localStorage.getItem('activeStore');
      return cached ? JSON.parse(cached) : null;
    } catch { return null; }
  })();

  const isConnected = activeStore?.connected === true
    || fallbackStore?.connected === true
    || stores.some(s => s.shop === currentShop && s.connected);
  const isOwner = activeStore?.user_role === 'owner'
    || activeStore?.user_role === 'admin'
    || fallbackStore?.user_role === 'owner'
    || fallbackStore?.user_role === 'admin'
    || false;

  // Removed excessive debug logging to prevent system hanging

  useEffect(() => {
    // Check if user has accepted stores
    const checkAcceptedStores = () => {
      const hasAccepted = localStorage.getItem('has_accepted_stores') === 'true';
      setHasAcceptedStores(hasAccepted);
    };

    // Check on mount
    checkAcceptedStores();

    // Listen for store acceptance changes
    const handleStoreAcceptanceChange = (event: any) => {
      setHasAcceptedStores(event.detail.hasAcceptedStores);
    };

    window.addEventListener('store_acceptance_changed', handleStoreAcceptanceChange);

    return () => {
      window.removeEventListener('store_acceptance_changed', handleStoreAcceptanceChange);
    };
  }, []);

  useEffect(() => {
    const fetchSidebarSettings = async () => {
      try {
        const shop = currentShop || localStorage.getItem("shop");
        if (!shop) return;

        const response = await axios.get("/user/advanced-settings", {
          params: { shop },
        });

        if (response.data.success && response.data.settings) {
          // Both 0 and "0" should disable it
          setIsSightEnabled(response.data.settings.sight_enabled != 0);
        }
      } catch (err) {
        console.error("Failed to load sidebar settings:", err);
      }
    };

    fetchSidebarSettings();

    // Listen for setting changes from the Settings page
    const handleSettingsUpdate = () => fetchSidebarSettings();
    window.addEventListener("aiIntelligenceChanged", handleSettingsUpdate);
    return () => window.removeEventListener("aiIntelligenceChanged", handleSettingsUpdate);
  }, [currentShop]);

  const navigationDisabled = disableNavigation;

  const handleNavItemClick = useCallback(
    (e?: React.MouseEvent) => {
      if (navigationDisabled) {
        e?.preventDefault?.();
        return;
      }

      // On mobile, close the sidebar after choosing any menu item.
      if (isMobileOpen) {
        toggleMobileSidebar();
      }
    },
    [isMobileOpen, navigationDisabled, toggleMobileSidebar],
  );


  useEffect(() => {
    const checkRazorpayIntegration = async () => {
      const shop = currentShop || localStorage.getItem("shop");
      if (!shop) return;
      try {
        const res = await axios.get("https://isight.netsights.ai/api/razorpay/integration-status", { params: { shop } });
        setHasRazorpayIntegration(res.data?.is_active === true);
      } catch {
        setHasRazorpayIntegration(false);
      }
    };
    checkRazorpayIntegration();
  }, [currentShop]);

  const handleScaleBoardClick = useCallback(
    (e: React.MouseEvent) => {
      if (navigationDisabled) {
        e.preventDefault();
        return;
      }
      if (isFreePlan) {
        // Redirect to Scale Board, then show upgrade popup there
        try {
          const from = `${location.pathname}${location.search}${location.hash}`;
          sessionStorage.setItem("scaleBoardReturnTo", from);
        } catch {
          /* ignore */
        }
        if (isMobileOpen) toggleMobileSidebar();
        return;
      }
      handleNavItemClick(e);
    },
    [
      handleNavItemClick,
      isFreePlan,
      isMobileOpen,
      location.hash,
      location.pathname,
      location.search,
      navigationDisabled,
      toggleMobileSidebar,
    ],
  );

  /**
   * Feature-gated click handler for nav items.
   * If the feature is disabled, prevents navigation and shows the upgrade modal.
   */
  const handleFeatureGatedClick = useCallback(
    (featureKey: string, featureLabel: string) => (e: React.MouseEvent) => {
      if (navigationDisabled) {
        e.preventDefault();
        return;
      }
      if (!hasFeature(featureKey)) {
        e.preventDefault();
        e.stopPropagation();
        openUpgradeModal(featureKey, featureLabel);
        return;
      }
      handleNavItemClick(e);
    },
    [navigationDisabled, hasFeature, openUpgradeModal, handleNavItemClick],
  );

  // Sights that should not be editable or deletable
  const isProtectedSight = (sight: { name: string; slug: string }) => {
    const nameLower = sight.name?.toLowerCase() ?? "";
    const slugLower = sight.slug?.toLowerCase() ?? "";
    const protectedNames = [
      "dashboard"
      // "sales",
      // "marketing",
      // "customers",
      // "inventory",
      // "blended",
      // "products",
      // "product",
      // "orders",
      // "order"
    ];
    return (
      protectedNames.includes(nameLower) || slugLower.includes("dashboard")
    );
  };

  // Load sights when shop is available
  useEffect(() => {
    if (currentShop) {
      loadSights(currentShop);
    }
  }, [currentShop, loadSights]);

  // Listen for store switch events to reload sights
  useEffect(() => {
    const handleStoreSwitch = (event: any) => {
      if (event.detail?.store && currentShop !== event.detail.store) {
        // Force reload sights for the new store
        loadSights(event.detail.store);
      }
    };

    window.addEventListener('store_switched', handleStoreSwitch);
    return () => window.removeEventListener('store_switched', handleStoreSwitch);
  }, [currentShop, loadSights]);

  const performCreateSight = async (name: string) => {
    if (navigationDisabled || !currentShop || !name.trim()) return;
    setIsCreatingSight(true);
    try {
      const newSight = await createSight(currentShop, {
        name: name.trim(),
        icon: "grid",
        widgets: [],
      });
      setNewSightName("");
      setIsAddingSight(false);
      setSightNameDuplicateConfirm(null);
      if (newSight && (newSight.md5_hash || newSight.slug)) {
        const identifier = newSight.md5_hash ?? newSight.slug;
        navigate(`/${encodeURIComponent(tenantKey)}/sight/${identifier}`);
      }
    } catch (error) {
      console.error("Error creating sight:", error);
      setAlertMessage("Failed to create sight");
    } finally {
      setIsCreatingSight(false);
    }
  };

  const handleAddSight = () => {
    if (navigationDisabled) return;
    const name = newSightName.trim();
    if (!name || !currentShop) return;
    const duplicate = sights.some(
      (s) => s.name.trim().toLowerCase() === name.toLowerCase(),
    );
    if (duplicate) {
      setSightNameDuplicateConfirm(name);
      return;
    }
    void performCreateSight(name);
  };

  const confirmAddDuplicateSight = () => {
    if (!sightNameDuplicateConfirm) return;
    void performCreateSight(sightNameDuplicateConfirm);
  };

  const handleEditSight = async (sightId: number) => {
    if (navigationDisabled) return;
    if (!editSightName.trim() || !currentShop) return;
    try {
      await updateSight(currentShop, sightId, { name: editSightName.trim() });
      setEditingSightId(null);
      setEditSightName("");
    } catch (error) {
      console.error("Error updating sight:", error);
      setAlertMessage("Failed to update sight");
    }
  };

  const confirmDeleteSight = async () => {
    if (navigationDisabled || !currentShop || !sightPendingDelete) return;
    setIsDeletingSight(true);
    try {
      const sightId = sightPendingDelete.id;
      const remainingSights = sights.filter((s) => s.id !== sightId);
      const firstSight = remainingSights[0];

      await deleteSight(currentShop, sightId);
      setSightPendingDelete(null);

      if (firstSight) {
        const identifier = firstSight.md5_hash ?? firstSight.slug;
        navigate(`/${encodeURIComponent(tenantKey)}/sight/${identifier}`);
      } else {
        navigate("");
      }
    } catch (error) {
      console.error("Error deleting sight:", error);
      setAlertMessage("Failed to delete sight");
    } finally {
      setIsDeletingSight(false);
    }
  };

  const [openSubmenu, setOpenSubmenu] = useState<{
    type: "main" | "others";
    index: number;
  } | null>(null);
  const [subMenuHeight, setSubMenuHeight] = useState<Record<string, number>>(
    {},
  );
  const subMenuRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const isActive = useCallback(
    (path: string) => location.pathname === path,
    [location.pathname],
  );

  useEffect(() => {
    let submenuMatched = false;
    ["main", "others"].forEach((menuType) => {
      const items = menuType === "main" ? navItems : othersItems;
      items.forEach((nav, index) => {
        if (nav.subItems) {
          nav.subItems.forEach((subItem) => {
            if (isActive(subItem.path)) {
              setOpenSubmenu({ type: menuType as "main" | "others", index });
              submenuMatched = true;
            }
          });
        }
      });
    });
    if (!submenuMatched) {
      setOpenSubmenu(null);
    }
  }, [location, isActive, tenantKey]);

  useEffect(() => {
    if (openSubmenu !== null) {
      const key = `${openSubmenu.type}-${openSubmenu.index}`;
      if (subMenuRefs.current[key]) {
        setSubMenuHeight((prevHeights) => ({
          ...prevHeights,
          [key]: subMenuRefs.current[key]?.scrollHeight || 0,
        }));
      }
    }
  }, [openSubmenu]);

  // Single source of truth for body scroll lock - avoids two effects
  // stomping on each other's "previous overflow" value.
  const originalOverflowRef = useRef<string | null>(null);

  useEffect(() => {
    const shouldLock =
      showPopup ||
      sightPendingDelete ||
      sightNameDuplicateConfirm ||
      protectedSightAction ||
      (isMobileOpen && window.innerWidth < 768);

    if (shouldLock) {
      if (originalOverflowRef.current === null) {
        originalOverflowRef.current = document.body.style.overflow;
      }
      document.body.style.overflow = "hidden";
    } else if (originalOverflowRef.current !== null) {
      document.body.style.overflow = originalOverflowRef.current;
      originalOverflowRef.current = null;
    }
  }, [
    showPopup,
    sightPendingDelete,
    sightNameDuplicateConfirm,
    protectedSightAction,
    isMobileOpen,
  ]);

  return (
    <>
      <aside
        className={`fixed top-14 left-0 z-50 flex h-[calc(100dvh-3.5rem)] flex-col overflow-hidden border-r border-gray-200 bg-white px-3 text-gray-900 transition-all duration-300 ease-in-out dark:border-[#FFFFFF0F] dark:bg-[#0d1828] lg:top-0 lg:h-screen
        ${isExpanded || isMobileOpen
            ? "w-[270px]"
            : isHovered
              ? "w-[270px]"
              : "w-[90px]"
          }
        ${isMobileOpen ? "translate-x-0" : "-translate-x-full"}
        lg:translate-x-0`}
        // onMouseEnter={() => !isExpanded && !isMobileOpen && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div
          className="hidden lg:mt-[5px] lg:mb-[5px] lg:flex h-[45px] shrink-0 min-w-0 border-b border-gray-200 dark:border-[#FFFFFF0F] -mx-3"
        // onMouseEnter={() =>
        //   !isExpanded && !isMobileOpen && setIsHovered(true)
        // }
        >
          <div
            className={`flex flex-1 items-center min-w-0  ${isExpanded || isHovered || isMobileOpen ? "justify-start px-6" : "justify-center"} ${!isExpanded && !isHovered && !isMobileOpen ? "cursor-pointer" : ""}`}
          >
            {isExpanded || isHovered || isMobileOpen ? (
              <>
                <img
                  className="dark:hidden h-8 w-auto max-w-[130px] object-contain object-left"
                  src="https://app.netsights.ai/images/logo/netsight-Black.svg"
                  alt="Logo"
                  width={130}
                  height={40}
                />
                <img
                  className="hidden dark:block h-8 w-auto max-w-[130px] object-contain object-left"
                  src="https://app.netsights.ai/images/logo/netsight-white.svg"
                  alt="Logo"
                  width={130}
                  height={40}
                />
              </>
            ) : (
              <>
                <img
                  src="https://app.netsights.ai/images/logo/Favicon.png"
                  alt="Logo"
                  width={25}
                  height={25}
                  className="dark:hidden"
                />
                <img
                  src="https://app.netsights.ai/images/logo/fevicondark.svg"
                  alt="Logo"
                  width={15}
                  height={15}
                  className="hidden dark:block"
                />
              </>
            )}
          </div>
        </div>

        {/* flex-1 min-h-0: lets this region scroll on mobile while body overflow is hidden */}
        <div className="-mx-3 flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-y-contain px-3 pb-24 duration-300 ease-linear no-scrollbar lg:pb-10">
          <nav className="mb-2 shrink-0">
            <div className="flex flex-col gap-4">
              {/* ── Sights Section ── */}
              {isConnected && (
                <div className="mt-1 ">
                  <ul className="flex flex-col gap-1 mb-1">
                    {/* Scale Board (treated like a Sight; stays on top) */}
                    {/* Show ScaleBoard if owner or has scaleboard_access permission */}
                    {(isOwner || checkPermission('scaleboard_access' as any)) && (
                      <li>
                        <NavLink
                          to={navigationDisabled ? "#" : "/scaleboard"}
                          end
                          onClick={handleScaleBoardClick}
                          title={
                            !(isExpanded || isHovered || isMobileOpen)
                              ? "Scale Board"
                              : undefined
                          }
                          className={({ isActive }) =>
                            `menu-item group w-full flex items-center ${isActive ? "menu-item-active" : "menu-item-inactive"} ${!isExpanded && !isHovered && !isMobileOpen
                              ? "lg:justify-start"
                              : "justify-start px-3"
                            }`
                          }
                        >
                          {(isExpanded || isHovered || isMobileOpen) && (
                            <span className="menu-item-icon-size transition-transform duration-200 group-hover:scale-110">
                              <ChartNoAxesCombinedIcon className="text-[#A3A3A3]" />
                            </span>
                          )}
                          {isExpanded || isHovered || isMobileOpen ? (
                            <span className="flex-1 truncate">Scaleboard</span>
                          ) : (
                            <span
                              className="flex-1 truncate font-medium text-center min-w-[1.25rem] text-xl"
                              style={{ fontFamily: "Inter", fontWeight: 200 }}
                            >
                              S
                            </span>
                          )}
                        </NavLink>
                      </li>
                    )}
                    {/* Category Deep-Dives (Like Scaleboard) - Show if owner has access or if user has scaleboard_access permission */}
                    {(isOwner || checkPermission('scaleboard_access' as any)) && [
                      { name: 'Marketing', path: '/scaleboard/marketing', icon: <Megaphone className="w-5 h-5 text-[#A3A3A3]" />, char: 'M', featureKey: '', featureLabel: 'Marketing' },
                      { name: 'Product', path: '/scaleboard/product', icon: <Package className="w-5 h-5 text-[#A3A3A3]" />, char: 'P', featureKey: '', featureLabel: 'Product' },
                      { name: 'Customer', path: '/scaleboard/customer', icon: <Users className="w-5 h-5 text-[#A3A3A3]" />, char: 'C', featureKey: '', featureLabel: 'Customer' },
                      { name: 'Store', path: '/scaleboard/store', icon: <Store className="w-5 h-5 text-[#A3A3A3]" />, char: 'S', featureKey: '', featureLabel: 'Store' },
                      { name: 'iSight AI', path: '/scaleboard/isight-ai', icon: <Sparkles className="w-5 h-5 text-[#A3A3A3]" />, char: 'I', featureKey: '', featureLabel: 'iSight AI' },
                      ...(hasRazorpayIntegration ? [{ name: 'Payment', path: '/scaleboard/payment', icon: <CreditCard className="w-5 h-5 text-[#A3A3A3] " />, char: '£', featureKey: 'razorpay', featureLabel: 'Payment Intelligence' }] : []),
                    ].map((item) => {
                      const fullPath = tenantKey ? `/${encodeURIComponent(tenantKey)}${item.path}` : item.path;
                      const marketingSubPaths = ['/scaleboard/ads-intelligence', '/scaleboard/spend-intelligence', '/scaleboard/organic-intelligence'];
                      const isMarketingSubTab = item.path === '/scaleboard/marketing' &&
                        marketingSubPaths.some(p => location.pathname === p || location.pathname.endsWith(p));
                      const itemClick = item.featureKey
                        ? handleFeatureGatedClick(item.featureKey, item.featureLabel)
                        : handleNavItemClick;
                      return (
                        <li key={item.path}>
                          <NavLink
                            to={navigationDisabled ? "#" : fullPath}
                            onClick={itemClick}
                            title={
                              !(isExpanded || isHovered || isMobileOpen)
                                ? item.name
                                : undefined
                            }
                            className={({ isActive }) =>
                              `menu-item group w-full flex items-center ${(isActive || isMarketingSubTab) ? "menu-item-active" : "menu-item-inactive"} ${!isExpanded && !isHovered && !isMobileOpen
                                ? "lg:justify-start"
                                : "justify-start px-3"
                              }`
                            }
                          >
                            {(isExpanded || isHovered || isMobileOpen) && (
                              <span className="menu-item-icon-size transition-transform duration-200 group-hover:scale-110">
                                {item.icon}
                              </span>
                            )}
                            {isExpanded || isHovered || isMobileOpen ? (
                              <span className="flex-1 truncate">{item.name}</span>
                            ) : (
                              <span
                                className="flex-1 truncate font-medium text-center min-w-[1.25rem] text-xl"
                                style={{ fontFamily: "Inter", fontWeight: 200 }}
                              >
                                {item.char}
                              </span>
                            )}
                          </NavLink>
                        </li>
                      );
                    })}
                  </ul>
                  {isSightEnabled && (isOwner || checkPermission('sight_access' as any)) && (
                    <>
                      <h2
                        className={`mb-3 text-xs uppercase flex leading-[20px] text-gray-400 ${!isExpanded && !isHovered
                          ? "lg:justify-center"
                          : "justify-start"
                          }`}
                      >
                        {isExpanded || isHovered || isMobileOpen ? "Sights" : <HorizontaLDots />}
                      </h2>

                      {/* Desktop: cap Sights height so Scaleboard stays visible; mobile: one outer scroll so footer + iSight stay reachable */}
                      <div
                        className="min-h-0 shrink-0 overflow-x-hidden overflow-y-visible overscroll-y-contain max-h-[30vh] lg:overflow-y-auto"
                        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
                      >
                        <ul className="flex flex-col gap-1">
                          <></>
                          {sights.map((sight) => (
                            <li key={sight.id}>
                              {editingSightId === sight.id ? (
                                /* Edit mode */
                                <div className="flex items-center gap-2 p-1 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                  <input
                                    type="text"
                                    value={editSightName}
                                    onChange={(e) => setEditSightName(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter")
                                        handleEditSight(sight.id);
                                      else if (e.key === "Escape") {
                                        setEditingSightId(null);
                                        setEditSightName("");
                                      }
                                    }}
                                    className="flex-1 px-1 py-1 text-sm border border-teal-500 rounded-md focus:outline-none focus:ring-1 focus:ring-teal-500 dark:bg-gray-700 dark:text-white dark:placeholder-white dark:border-teal-400"
                                    autoFocus
                                  />
                                  <button
                                    onClick={() => handleEditSight(sight.id)}
                                    className="p-[5px] text-white bg-teal-600 rounded-md hover:bg-gray-700"
                                    title="Save"
                                  >
                                    <svg
                                      className="w-3 h-3"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M5 13l4 4L19 7"
                                      />
                                    </svg>
                                  </button>
                                  <button
                                    onClick={() => {
                                      setEditingSightId(null);
                                      setEditSightName("");
                                    }}
                                    className="p-1 text-red-600 hover:text-red-700 dark:text-red-400 text-sm border border-red-500 rounded-md"
                                    title="Cancel"
                                  >
                                    <svg
                                      className="w-3 h-3"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M6 18L18 6M6 6l12 12"
                                      />
                                    </svg>
                                  </button>
                                </div>
                              ) : (
                                /* View mode */
                                <div className="flex items-center gap-2">
                                  <NavLink
                                    to={
                                      navigationDisabled
                                        ? "#"
                                        : `/${encodeURIComponent(tenantKey)}/sight/${sight.md5_hash ?? sight.slug}`
                                    }
                                    onClick={handleNavItemClick}
                                    title={
                                      !(isExpanded || isHovered || isMobileOpen)
                                        ? sight.name
                                        : undefined
                                    }
                                    className={({ isActive }) =>
                                      `menu-item group flex-1 min-w-0 flex items-center ${isActive ? "menu-item-active" : "menu-item-inactive"} ${!isExpanded && !isHovered && !isMobileOpen
                                        ? "lg:justify-start"
                                        : "justify-start px-3"
                                      }`
                                    }
                                  >
                                    {(isExpanded || isHovered || isMobileOpen) && (
                                      <span className="menu-item-icon-size transition-transform duration-200 group-hover:scale-110">
                                        <GridIcon className="text-[#A3A3A3]" />
                                      </span>
                                    )}
                                    {isExpanded || isHovered || isMobileOpen ? (
                                      <>
                                        <span className="flex-1 truncate">
                                          {sight.name}
                                        </span>
                                        {sight.widget_count !== undefined && (
                                          <span className="text-xs bg-gray-100 dark:bg-[#162640] px-2 py-0.5 rounded-full">
                                            {sight.widget_count}
                                          </span>
                                        )}
                                      </>
                                    ) : (
                                      <span
                                        className="flex-1 truncate font-medium text-center min-w-[1.25rem] text-xl"
                                        style={{
                                          fontFamily: "Inter",
                                          fontWeight: 200,
                                        }}
                                      >
                                        {sight.name
                                          ? sight.name.charAt(0).toUpperCase()
                                          : ""}
                                      </span>
                                    )}
                                  </NavLink>

                                  {/* Edit / Delete buttons */}
                                  {(isExpanded || isHovered || isMobileOpen) && (
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                      <button
                                        type="button"
                                        disabled={isProtectedSight(sight)}
                                        onClick={() => {
                                          if (navigationDisabled) return;
                                          if (isProtectedSight(sight)) return;
                                          setEditingSightId(sight.id);
                                          setEditSightName(sight.name);
                                        }}
                                        className={`p-1 ${isProtectedSight(sight)
                                          ? "text-gray-300 cursor-not-allowed dark:text-gray-600"
                                          : "text-gray-400 hover:text-teal-600 dark:hover:text-teal-400"
                                          }`}
                                        title={isProtectedSight(sight) ? "This sight cannot be edited" : "Edit"}
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                                          />
                                        </svg>
                                      </button>
                                      <button
                                        onClick={() => {
                                          if (isProtectedSight(sight)) {
                                            return;
                                          }
                                          setSightPendingDelete({
                                            id: sight.id,
                                            name: sight.name,
                                          });
                                        }}
                                        className={`p-1 ${isProtectedSight(sight)
                                          ? "text-gray-300 cursor-not-allowed dark:text-gray-600"
                                          : "text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                                          }`}
                                        title={isProtectedSight(sight) ? "This sight cannot be deleted" : "Delete"}
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                          />
                                        </svg>
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </nav>

          {/* Add Sight + footer links: same scroll column as Sights so flex-1 doesn't leave a gap above Add Sight */}
          {isSightEnabled && (isOwner || hasAcceptedStores) && (isExpanded || isHovered || isMobileOpen) && isConnected && (
            <div className="shrink-0 px-1 pb-2 -mb-2">
              {isAddingSight || sights.length === 0 ? (
                <div className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-[#0d1828] rounded-lg">
                  <input
                    type="text"
                    value={newSightName}
                    onChange={(e) => setNewSightName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleAddSight();
                      else if (e.key === "Escape") {
                        setIsAddingSight(false);
                        setNewSightName("");
                      }
                    }}
                    placeholder="Enter sight name..."
                    className="flex-1 px-1 py-1 w-[9rem] text-sm border border-teal-500 rounded-md focus:outline-none focus:ring-1 focus:ring-teal-500 dark:bg-[#111f33] dark:text-white dark:placeholder-white dark:border-teal-400"
                    autoFocus
                  />
                  <button
                    onClick={handleAddSight}
                    disabled={isCreatingSight}
                    className="p-[5px] text-white bg-teal-600 rounded-md hover:bg-gray-700 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Add"
                  >
                    <svg
                      className="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={() => {
                      setIsAddingSight(false);
                      setNewSightName("");
                    }}
                    className="p-1 text-red-600 hover:text-red-700 dark:text-red-400 text-sm border border-red-500 rounded-md"
                    title="Cancel"
                  >
                    <svg
                      className="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => {
                    // if (featureLimitReached('sight_limit', sights.length)) {
                    //   openUpgradeModal('sight_limit', 'Sight');
                    //   return;
                    // }
                    setIsAddingSight(true);
                  }}
                  className="group w-full p-2 text-teal-600 dark:text-teal-400
                           bg-teal-50 dark:bg-teal-900/20
                           rounded-[6px]
                           hover:bg-teal-100 dark:hover:!bg-teal-500 dark:hover:text-white
                           transition-colors"
                >
                  <div className="flex items-center justify-center space-x-2">
                    <svg
                      className="w-5 h-5 transition-transform duration-300 group-hover:rotate-90"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                      />
                    </svg>
                    <span className="font-medium">Add Sight</span>
                  </div>
                </button>
              )}
            </div>
          )}


          {/* Show Notifications only if user has notification_access permission and store is connected */}
          {isConnected && checkPermission('notification_access' as any) && (
            <ul className="mt-2 flex shrink-0 flex-col gap-2">
              <div
                className="shrink-0"
                style={{
                  cursor: navigationDisabled ? "not-allowed" : "pointer",
                  opacity: navigationDisabled ? 0.5 : 1,
                  pointerEvents: navigationDisabled ? "none" : "auto",
                }}
              >
                <li>
                  <NavLink
                    to={navigationDisabled ? "#" : "/notification"}
                    onClick={handleNavItemClick}
                    className={({ isActive: isActiveRoute }) =>
                      `menu-item group w-full flex items-center ${isActiveRoute ? "menu-item-active" : "menu-item-inactive"} ${!isExpanded && !isHovered && !isMobileOpen
                        ? "lg:justify-start "
                        : "justify-start px-3"
                      }`
                    }
                  >
                    <span
                      className={`menu-item-icon-size origin-top [transform-box:fit-content] group-hover:animate-bell-ring ${location.pathname.startsWith("/notification")
                        ? "menu-item-icon-active"
                        : "menu-item-icon-inactive"
                        }${!(isExpanded || isHovered || isMobileOpen) ? " ml-2" : ""}`}
                    >
                      <NotificationIcondark className="h-5 w-5 block dark:hidden text-[#A3A3A3]" />
                      <NotificationIconlight className="h-5 w-5 hidden dark:block text-[#A3A3A3]" />
                    </span>
                    {(isExpanded || isHovered || isMobileOpen) && (
                      <span className="flex-1 truncate">Notification</span>
                    )}
                  </NavLink>
                </li>
              </div>
            </ul>
          )}

          {/* ── iSight v2 Chat ── */}
          {/* {isConnected && (isOwner || checkPermission('isight_access' as any)) && (
            <ul className="mt-2 flex shrink-0 flex-col gap-2">
              <div
                className="shrink-0"
                style={{
                  cursor: navigationDisabled ? "not-allowed" : "pointer",
                  opacity: navigationDisabled ? 0.5 : 1,
                  pointerEvents: navigationDisabled ? "none" : "auto",
                }}
              >
                <li>
                  <NavLink
                    to={navigationDisabled ? "#" : "/isight-v2"}
                    onClick={handleNavItemClick}
                    className={({ isActive: isActiveRoute }) =>
                      `menu-item group w-full flex items-center ${isActiveRoute ? "menu-item-active" : "menu-item-inactive"} ${!isExpanded && !isHovered && !isMobileOpen
                        ? "lg:justify-start "
                        : "justify-start px-3"
                      }`
                    }
                  >
                    <span
                      className={`menu-item-icon-size ${location.pathname.startsWith("/isight-v2")
                        ? "menu-item-icon-active"
                        : "menu-item-icon-inactive"
                        }${!(isExpanded || isHovered || isMobileOpen) ? " ml-2" : ""}`}
                    >
                      <Sparkles className="h-5 w-5" />
                    </span>
                    {(isExpanded || isHovered || isMobileOpen) && (
                      <span className="flex-1 truncate">iSight v2</span>
                    )}
                  </NavLink>
                </li>
              </div>
            </ul>
          )} */}

          {/* ── iSight Open Button (hidden when Settings → AI → isight intelligence is off) ── */}
          {isConnected && (isOwner || checkPermission('isight_access' as any)) && (
            <div className="shrink-0 mt-1">
              <hr className=" border-gray-200 dark:border-[#FFFFFF0F]" />
              <div>
                <IsightOpenButton
                  // variant="sidebar"
                  navigationDisabled={navigationDisabled}
                />
              </div>
              <hr className=" border-gray-200 dark:border-[#FFFFFF0F]" />
              {/* Scalekit (d2c dashboard) — SSO bridge; same external-tool pattern */}
              <div className="mt-1">
                <ScalekitOpenButton navigationDisabled={navigationDisabled} />
              </div>
            </div>
          )}
        </div>
      </aside>

      {sightPendingDelete && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:border-[#FFFFFF0F] dark:bg-[#111f33]">
            <div className="mb-4 flex items-center justify-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900">
                <svg
                  className="h-6 w-6 text-red-600 dark:text-red-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
            </div>
            <h3 className="mb-2 text-center text-lg font-semibold text-gray-900 dark:text-white">
              Delete sight
            </h3>
            <p className="mb-6 text-center text-gray-600 dark:text-gray-300">
              Are you sure you want to delete &quot;{sightPendingDelete.name}
              &quot;? This cannot be undone.
            </p>
            <div className="flex justify-center space-x-3">
              <button
                type="button"
                onClick={() => {
                  if (!isDeletingSight) setSightPendingDelete(null);
                }}
                disabled={isDeletingSight}
                className="rounded-[6px] border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-[#FFFFFF0F] dark:text-gray-400 dark:hover:bg-gray-950"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeleteSight}
                disabled={isDeletingSight}
                className="flex items-center justify-center rounded-[6px] bg-error-600 px-4 py-2 text-white transition-colors hover:bg-error-700 disabled:opacity-50 dark:hover:bg-error-700"
              >
                {isDeletingSight ? (
                  <>
                    {/* <Loader className="mr-2 h-5 w-5" /> */}
                    Deleting...
                  </>
                ) : (
                  "Delete"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {sightNameDuplicateConfirm && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:border-[#FFFFFF0F] dark:bg-[#111f33]">
            <div className="mb-4 flex items-center justify-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900">
                <svg
                  className="h-6 w-6 text-red-600 dark:text-red-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
            </div>
            <h3 className="mb-2 text-center text-lg font-semibold text-gray-900 dark:text-white">
              Sight name already exists
            </h3>
            <p className="mb-6 text-center text-gray-600 dark:text-gray-300">
              A sight named &quot;{sightNameDuplicateConfirm}&quot; is already
              in your list. Do you want to use this name again?
            </p>
            <div className="flex justify-center space-x-3">
              <button
                type="button"
                onClick={() => {
                  if (!isCreatingSight) setSightNameDuplicateConfirm(null);
                }}
                disabled={isCreatingSight}
                className="rounded-[6px] border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-[#FFFFFF0F] dark:text-gray-400 dark:hover:bg-gray-950"
              >
                No, cancel
              </button>
              <button
                type="button"
                onClick={confirmAddDuplicateSight}
                disabled={isCreatingSight}
                className="flex items-center justify-center rounded-[6px] bg-teal-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-teal-700 disabled:opacity-50 dark:hover:bg-teal-700"
              >
                {isCreatingSight ? (
                  <>
                    {/* <Loader className="mr-2 h-5 w-5" /> */}
                    Creating...
                  </>
                ) : (
                  "Yes, use this name"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <DeleteConfirmModal
        open={Boolean(protectedSightAction)}
        onClose={() => setProtectedSightAction(null)}
        title={`This sight cannot be ${protectedSightAction ?? "edited"}`}
        description={
          protectedSightAction
            ? `This is a protected sight, so it cannot be ${protectedSightAction}.`
            : undefined
        }
        confirmLabel="OK"
        onConfirm={() => setProtectedSightAction(null)}
        zIndexBase={1200}
      />
      <DeleteConfirmModal
        open={Boolean(alertMessage)}
        onClose={() => setAlertMessage(null)}
        title="Unable to continue"
        description={alertMessage ?? undefined}
        confirmLabel="OK"
        onConfirm={() => setAlertMessage(null)}
        zIndexBase={1200}
      />

      {/* ── Book a Demo Modal ── */}
      {
        showPopup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-99999">
            <div className="w-full max-w-2xl max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-3rem)] md:max-h-[calc(100vh-6rem)] min-h-[75vh] md:min-h-[70vh] rounded-lg bg-white shadow-xl px-4">
              <div className="flex items-center justify-between z-50 relative">
                <div className="flex items-center gap-4">
                  <h2 className="text-xl p-4 font-bold">
                    Pick your time slot for expert assistance
                  </h2>
                </div>
                <button
                  onClick={() => setShowPopup(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
              <div>
                <iframe
                  className="calendly-inline-widget w-full h-[calc(100vh-18rem)]"
                  src="https://calendly.com/netsightsai/book-a-demo"
                  data-url="https://calendly.com/netsightsai/book-a-demo"
                />
                <script
                  type="text/javascript"
                  src="https://assets.calendly.com/assets/external/widget.js"
                  async
                />
              </div>
            </div>
          </div>
        )
      }
    </>
  );
}
